// Browser fallback for `window.electronAPI`. Installed at app boot when
// the real Electron preload bridge is absent (i.e. running in a browser).
// Mirrors the exact same shape & return contracts so the call sites in
// the React app don't care which environment they're in.

const isFileLike = (x) => typeof File !== "undefined" && x instanceof File;

// One hidden <input type="file"> reused for every "Open dialog" call. Browsers
// only fire `change` when the user actually picks something — for cancel we
// rely on the `cancel` event (modern browsers) and resolve to canceled.
let pickerInput = null;
function pickFile(accept) {
  return new Promise((resolve) => {
    if (!pickerInput) {
      pickerInput = document.createElement("input");
      pickerInput.type = "file";
      pickerInput.style.position = "fixed";
      pickerInput.style.left = "-9999px";
      document.body.appendChild(pickerInput);
    }
    pickerInput.value = "";
    pickerInput.accept = accept || "";
    let settled = false;
    const onChange = () => {
      if (settled) return;
      settled = true;
      cleanup();
      const file = pickerInput.files && pickerInput.files[0];
      resolve(file || null);
    };
    const onCancel = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(null);
    };
    const cleanup = () => {
      pickerInput.removeEventListener("change", onChange);
      pickerInput.removeEventListener("cancel", onCancel);
    };
    pickerInput.addEventListener("change", onChange, { once: true });
    pickerInput.addEventListener("cancel", onCancel, { once: true });
    pickerInput.click();
  });
}

async function postFile(url, file, extraFields) {
  const form = new FormData();
  form.append("file", file, file.name);
  if (extraFields) {
    for (const [k, v] of Object.entries(extraFields)) {
      if (v != null) form.append(k, String(v));
    }
  }
  const res = await fetch(url, { method: "POST", body: form, credentials: "include" });
  if (res.status === 401) {
    if (!window.location.hash.startsWith("#/login")) {
      window.location.hash = "#/login";
    }
    return { success: false, error: "Unauthorized" };
  }
  return res.json();
}

async function postJSON(url, body) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
    credentials: "include",
  });
  if (res.status === 401) {
    if (!window.location.hash.startsWith("#/login")) {
      window.location.hash = "#/login";
    }
    return { success: false, error: "Unauthorized" };
  }
  return res.json();
}

// Common pattern: caller may pass nothing (open picker), a File, or — in
// legacy Electron call sites — a string path. In the browser the string
// path is meaningless and we treat it as "no file".
async function fileOrPick(maybeFile, accept) {
  if (isFileLike(maybeFile)) return maybeFile;
  return pickFile(accept);
}

const ACCEPT_RTF = ".rtf";
const ACCEPT_CERAD = ".xls";
const ACCEPT_GERIA = ".xls";
const ACCEPT_NOSGER = ".xlsm,.xlsx,.xls";
const ACCEPT_BDQ = ".xlsx,.xlsm,.xls";

export const webAPI = {
  // ----- RTF -----
  openRTFFileDialog: async (lang) => {
    const file = await pickFile(ACCEPT_RTF);
    if (!file) return { success: false, canceled: true };
    return postFile("/api/rtf/parse", file, { lang });
  },
  parseDroppedRTFFile: async (fileOrPath, lang) => {
    const file = await fileOrPick(fileOrPath, ACCEPT_RTF);
    if (!file) return { success: false, canceled: true };
    return postFile("/api/rtf/parse", file, { lang });
  },

  // ----- CERAD -----
  parseCeradXLS: async () => {
    const file = await pickFile(ACCEPT_CERAD);
    if (!file) return { success: false, canceled: true };
    return postFile("/api/xls/cerad", file);
  },
  parseCeradXLSFromPath: async (fileOrPath) => {
    const file = await fileOrPick(fileOrPath, ACCEPT_CERAD);
    if (!file) return { success: false, canceled: true };
    return postFile("/api/xls/cerad", file);
  },

  // ----- GERIA-12 -----
  parseGeria12XLS: async () => {
    const file = await pickFile(ACCEPT_GERIA);
    if (!file) return { success: false, canceled: true };
    return postFile("/api/xls/geria12", file);
  },
  parseGeria12XLSFromPath: async (fileOrPath) => {
    const file = await fileOrPick(fileOrPath, ACCEPT_GERIA);
    if (!file) return { success: false, canceled: true };
    return postFile("/api/xls/geria12", file);
  },

  // ----- NOSGER -----
  parseNosgerXLS: async () => {
    const file = await pickFile(ACCEPT_NOSGER);
    if (!file) return { success: false, canceled: true };
    return postFile("/api/xls/nosger", file);
  },
  parseNosgerXLSFromPath: async (fileOrPath) => {
    const file = await fileOrPick(fileOrPath, ACCEPT_NOSGER);
    if (!file) return { success: false, canceled: true };
    return postFile("/api/xls/nosger", file);
  },

  // ----- BDQ -----
  parseBdqXLS: async () => {
    const file = await pickFile(ACCEPT_BDQ);
    if (!file) return { success: false, canceled: true };
    return postFile("/api/xls/bdq", file);
  },
  parseBdqXLSFromPath: async (fileOrPath) => {
    const file = await fileOrPick(fileOrPath, ACCEPT_BDQ);
    if (!file) return { success: false, canceled: true };
    return postFile("/api/xls/bdq", file);
  },

  parseXLSAuto: async (fileOrPath) => {
    const file = await fileOrPick(fileOrPath, ".xls,.xlsx,.xlsm");
    if (!file) return { success: false, canceled: true };
    return postFile("/api/xls/auto", file);
  },

  // ----- DuckDB parquet -----
  queryParquet: async (relPath) => {
    return postJSON("/api/parquet/query", { relPath });
  },

  // ----- Excel scoresheet build → triggers a browser download -----
  // Electron returns { success, filePath }. We swap filePath for the
  // downloaded filename — call sites just show this string back to the user.
  openAndEditExcel: async (cellEdits, hiddenRows, visibleRows, emptyHeaderRows, lang, suggestedName, mergeRanges) => {
    try {
      const res = await fetch("/api/excel/build", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ cellEdits, hiddenRows, visibleRows, emptyHeaderRows, lang, suggestedName, mergeRanges }),
      });
      if (res.status === 401) {
        if (!window.location.hash.startsWith("#/login")) {
          window.location.hash = "#/login";
        }
        return { success: false, error: "Unauthorized" };
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Build failed" }));
        return { success: false, error: err.error };
      }
      const blob = await res.blob();
      const name = suggestedName || (lang === "de" ? "profilBlatt.xlsx" : "feuilleProfil.xlsx");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return { success: true, filePath: name };
    } catch (err) {
      return { success: false, error: err.message || "Download failed" };
    }
  },
};

// Auth helpers (not part of electronAPI; used by Login screen + AuthGuard)
export async function checkAuth() {
  try {
    const res = await fetch("/api/me", { credentials: "include" });
    const data = await res.json();
    return !!data.authenticated;
  } catch (_) {
    return false;
  }
}

export async function login(password) {
  const res = await fetch("/api/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ password }),
  });
  const data = await res.json().catch(() => ({}));
  return { success: !!data.success, error: data.error };
}

export async function logout() {
  await fetch("/api/logout", { method: "POST", credentials: "include" });
}

// Install on window only when the real Electron bridge isn't present.
// Idempotent so React StrictMode double-effect doesn't matter.
export function installWebAPIIfNeeded() {
  if (typeof window === "undefined") return false;
  if (window.electronAPI) return false;
  window.electronAPI = webAPI;
  window.__isWebMode = true;
  return true;
}
